import { Fragment, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { FeeItem } from '@/lib/api-types'
import { feeTotals, formatTokenAmount, formatUsd } from '@/lib/format'

/**
 * The fees a finished flow paid. One line shows as it is; more than one
 * shows as the total only, with the lines behind a chevron (John, Nov 2
 * 2026: the receipts were long and spammy). Amount in the currency it was
 * paid in and, when known, its dollar value; no dollar column at all when
 * no line has a value (John, Oct 31 2026). Nothing when there were none or
 * they are not known yet.
 */
export function FeeReceipt({ items, className = '' }: { items: FeeItem[] | null | undefined; className?: string }) {
  const [open, setOpen] = useState(false)
  if (!items || items.length === 0) return null
  const dollars = items.some((item) => item.usd !== null)
  const cols = dollars ? 'grid-cols-[1fr_auto_auto]' : 'grid-cols-[1fr_auto]'
  const lines = items.map((item, index) => (
    <Fragment key={`${item.label}-${index}`}>
      <dt className="text-muted">{item.label}</dt>
      <dd className="text-right tabular-nums text-muted">
        {formatTokenAmount(Number(item.amountRaw) / 10 ** item.decimals, item.symbol, item.decimals)} {item.symbol}
      </dd>
      {dollars ? <dd className="min-w-12 text-right tabular-nums text-muted">{item.usd !== null ? formatUsd(item.usd) : ''}</dd> : null}
    </Fragment>
  ))
  if (items.length === 1) return <dl className={`grid gap-x-4 gap-y-1 text-xs ${cols} ${className}`}>{lines}</dl>
  const total = feeTotals(items)
  return (
    <div className={`text-xs ${className}`}>
      <button type="button" className={`grid w-full gap-x-4 text-left ${cols}`} onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span className="flex items-center gap-1 font-medium">
          Total fees
          <ChevronDown className={`size-3.5 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
        <span className="text-right tabular-nums font-medium">{total.amounts}</span>
        {dollars ? <span className="min-w-12 text-right tabular-nums font-medium">{total.usd !== null ? formatUsd(total.usd) : ''}</span> : null}
      </button>
      {open ? <dl className={`mt-1 grid gap-x-4 gap-y-1 border-t border-line pt-1 ${cols}`}>{lines}</dl> : null}
    </div>
  )
}
